import React from 'react';
import { View, Text, Svg, Circle } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { DirectorDeepProfile, ReportData } from '../tokens/types';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';

type Band = 'pos' | 'warn' | 'neg' | 'muted';
const scoreBand = (score?: number): Band => {
    if (score == null) return 'muted';
    if (score >= 750) return 'pos';
    if (score >= 650) return 'warn';
    return 'neg';
};
const scoreLabel = (score?: number) => {
    const band = scoreBand(score);
    if (band === 'pos') return 'Excellent';
    if (band === 'warn') return 'Fair';
    if (band === 'neg') return 'Poor';
    return 'N/A';
};
const bandTone = (b: Band): 'LOW' | 'MEDIUM' | 'HIGH' | 'muted' =>
    b === 'pos' ? 'LOW' : b === 'warn' ? 'MEDIUM' : b === 'neg' ? 'HIGH' : 'muted';
const bandColor = (b: Band) => (b === 'pos' ? colors.pos : b === 'warn' ? colors.warn : b === 'neg' ? colors.neg : colors.muted);

const initials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join('') || '—';

const AVATAR_PALETTE: Array<{ bg: string; ink: string }> = [
    { bg: '#CCFBF1', ink: '#064E3B' },
    { bg: '#DBEAFE', ink: '#1E3A8A' },
    { bg: '#FEF3C7', ink: '#92400E' },
    { bg: '#FCE7F3', ink: '#9D174D' },
    { bg: '#E0E7FF', ink: '#3730A3' },
    { bg: '#D1FAE5', ink: '#065F46' },
    { bg: '#FEE2E2', ink: '#991B1B' },
    { bg: '#F5F3FF', ink: '#5B21B6' },
];
const avatarTone = (key: string) => {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

// Bureau scores in India: 300–900. Map to 0..1 fill ratio for the gauge.
const scoreFraction = (score?: number) => {
    if (score == null) return 0;
    return Math.max(0, Math.min(1, (score - 300) / 600));
};

const buildSummary = (profiles: DirectorDeepProfile[]) => {
    const total = profiles.length;
    const scored = profiles.filter((p) => typeof p.creditScore === 'number');
    const avgScore = scored.length ? Math.round(scored.reduce((s, p) => s + (p.creditScore ?? 0), 0) / scored.length) : undefined;
    const totalCases = profiles.reduce((s, p) => s + (p.legalCasesCount ?? 0), 0);
    const directorsWithCases = profiles.filter((p) => (p.legalCasesCount ?? 0) > 0).length;
    const maxBoards = profiles.reduce((m, p) => Math.max(m, p.currentEntities?.length ?? 0), 0);
    return { total, avgScore, totalCases, directorsWithCases, maxBoards };
};

// ──────────────────────────── PDF ────────────────────────────

const PdfStatBox: React.FC<{ label: string; value: string; tone?: 'default' | 'pos' | 'neg' | 'brand' | 'warn' }> = ({
    label,
    value,
    tone = 'default',
}) => {
    const ink =
        tone === 'pos'
            ? colors.pos
            : tone === 'neg'
            ? colors.neg
            : tone === 'warn'
            ? colors.warn
            : tone === 'brand'
            ? colors.brand
            : colors.ink00;
    const bg =
        tone === 'pos'
            ? colors.posSoft
            : tone === 'neg'
            ? colors.negSoft
            : tone === 'warn'
            ? colors.warnSoft
            : tone === 'brand'
            ? colors.brandSoft
            : colors.surface;
    return (
        <View
            style={{
                flexGrow: 1,
                flexBasis: 80,
                paddingVertical: space.sp2,
                paddingHorizontal: space.sp3,
                borderRadius: 4,
                backgroundColor: bg,
            }}
        >
            <Text
                style={{
                    fontSize: typeScale.micro,
                    color: colors.ink40,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontFamily: fontFamily.bodyBold,
                }}
            >
                {label}
            </Text>
            <Text style={{ fontSize: typeScale.metricMd, color: ink, fontFamily: fontFamily.bodyBold, marginTop: 2 }}>{value}</Text>
        </View>
    );
};

const PdfScoreGauge: React.FC<{ score?: number }> = ({ score }) => {
    const band = scoreBand(score);
    const color = bandColor(band);
    const r = 18;
    const c = 2 * Math.PI * r;
    const frac = scoreFraction(score);
    const dash = Math.max(0.001, c * frac);
    return (
        <View style={{ alignItems: 'center', justifyContent: 'center', position: 'relative', width: 48, height: 48 }}>
            <Svg width={48} height={48} viewBox="0 0 48 48">
                <Circle cx={24} cy={24} r={r} stroke={colors.hairline} strokeWidth={4} fill="none" />
                {frac > 0 ? (
                    <Circle
                        cx={24}
                        cy={24}
                        r={r}
                        stroke={color}
                        strokeWidth={4}
                        fill="none"
                        strokeDasharray={`${dash} ${c - dash}`}
                        transform="rotate(-90 24 24)"
                        strokeLinecap="round"
                    />
                ) : null}
            </Svg>
            <View
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 48,
                    height: 48,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Text
                    style={{
                        fontSize: typeScale.h4,
                        fontFamily: fontFamily.bodyBold,
                        color,
                        textAlign: 'center',
                    }}
                >
                    {score ?? '—'}
                </Text>
            </View>
        </View>
    );
};

const PdfDirectorCard: React.FC<{ p: DirectorDeepProfile }> = ({ p }) => {
    const band = scoreBand(p.creditScore);
    const tone = avatarTone(p.name);
    const hasCases = (p.legalCasesCount ?? 0) > 0;
    const otherBoards = p.currentEntities?.slice(0, 3) ?? [];
    return (
        <View
            style={{
                flex: 1,
                padding: space.sp3,
                borderWidth: 0.5,
                borderColor: hasCases ? colors.neg : colors.rule,
                borderLeftWidth: 2,
                borderLeftColor: hasCases ? colors.neg : bandColor(band),
                borderRadius: 4,
                backgroundColor: colors.paper,
            }}
        >
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: space.sp2 }}>
                <View
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        backgroundColor: tone.bg,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: space.sp2,
                    }}
                >
                    <Text style={{ fontSize: 11, fontFamily: fontFamily.bodyBold, color: tone.ink }}>{initials(p.name)}</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: typeScale.h4, fontFamily: fontFamily.bodyBold, color: colors.ink00 }}>{p.name}</Text>
                    <Text style={{ fontSize: typeScale.caption, color: colors.ink40 }}>{p.designation}</Text>
                </View>
                <PdfScoreGauge score={p.creditScore} />
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                <PdfChip tone={bandTone(band)}>{scoreLabel(p.creditScore)}</PdfChip>
                {p.creditBureau ? <PdfChip tone="muted">{p.creditBureau}</PdfChip> : null}
                {p.totalAccounts != null ? <PdfChip tone="muted">{p.totalAccounts} accounts</PdfChip> : null}
                {p.dpdAccounts != null && p.dpdAccounts > 0 ? <PdfChip tone="HIGH">{p.dpdAccounts} DPD acc.</PdfChip> : null}
                {p.maxDpds != null && p.maxDpds > 0 ? (
                    <PdfChip tone={p.maxDpds > 30 ? 'HIGH' : 'MEDIUM'}>{p.maxDpds}d max DPD</PdfChip>
                ) : null}
                {p.currentOverdue != null && p.currentOverdue > 0 ? (
                    <PdfChip tone="HIGH">₹{Math.round(p.currentOverdue).toLocaleString('en-IN')} overdue</PdfChip>
                ) : null}
                {p.enquiriesInPast90Days != null && p.enquiriesInPast90Days > 0 ? (
                    <PdfChip tone="muted">{p.enquiriesInPast90Days} enq · 90d</PdfChip>
                ) : null}
                {hasCases ? (
                    <PdfChip tone="HIGH">
                        {p.legalCasesCount} legal case{(p.legalCasesCount ?? 0) > 1 ? 's' : ''}
                    </PdfChip>
                ) : null}
                {p.currentEntities?.length ? (
                    <PdfChip tone="muted">
                        {p.currentEntities.length} board{p.currentEntities.length > 1 ? 's' : ''}
                    </PdfChip>
                ) : null}
                {p.phone ? <PdfChip tone="muted">{p.phone}</PdfChip> : null}
            </View>
            {otherBoards.length ? (
                <View style={{ marginTop: space.sp2 }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: colors.ink40,
                            marginBottom: 2,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        Other boards
                    </Text>
                    {otherBoards.map((e, i) => {
                        const label = `${e.name}${e.role ? ` — ${e.role}` : ''}`;
                        return (
                            <Text key={i} style={{ fontSize: typeScale.caption, color: colors.ink10, marginTop: 1, lineHeight: 1.35 }}>
                                • {label.length > 90 ? label.slice(0, 90).trim() + '…' : label}
                            </Text>
                        );
                    })}
                    {(p.currentEntities?.length ?? 0) > otherBoards.length ? (
                        <Text style={{ fontSize: typeScale.micro, color: colors.ink40, marginTop: 2 }}>
                            +{(p.currentEntities?.length ?? 0) - otherBoards.length} more
                        </Text>
                    ) : null}
                </View>
            ) : null}
        </View>
    );
};

const PdfDirectorsDeep: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const dd = data.directorsDeep;
    if (!dd) return null;
    const summary = buildSummary(dd.profiles);
    return (
        <PdfSection
            kicker="Section · Directors"
            title="Director bureau & regulatory deep-dive"
            subtitle="Per-director CIBIL / Equifax score bands, legal exposure and concurrent directorships."
            index={index}
            breakBefore={breakBefore}
        >
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.sp2, marginTop: space.sp2 }}>
                <PdfStatBox label="Profiled" value={String(summary.total)} tone="brand" />
                {summary.avgScore != null ? (
                    <PdfStatBox
                        label="Avg score"
                        value={String(summary.avgScore)}
                        tone={summary.avgScore >= 750 ? 'pos' : summary.avgScore >= 650 ? 'warn' : 'neg'}
                    />
                ) : null}
                <PdfStatBox label="Legal exposure" value={String(summary.totalCases)} tone={summary.totalCases > 0 ? 'neg' : 'default'} />
                {summary.directorsWithCases > 0 ? (
                    <PdfStatBox label="Directors w/ cases" value={String(summary.directorsWithCases)} tone="neg" />
                ) : null}
                {summary.maxBoards > 0 ? <PdfStatBox label="Max boards" value={String(summary.maxBoards)} /> : null}
            </View>
            <View style={{ marginTop: space.sp4 }}>
                {Array.from({ length: Math.ceil(dd.profiles.length / 2) }, (_, ri) => (
                    <View key={ri} style={{ flexDirection: 'row', gap: space.sp2, marginBottom: space.sp2 }}>
                        {dd.profiles.slice(ri * 2, ri * 2 + 2).map((p, i) => (
                            <PdfDirectorCard key={i} p={p} />
                        ))}
                    </View>
                ))}
            </View>
        </PdfSection>
    );
};

// ──────────────────────────── Screen ────────────────────────────

const ScreenStatBox: React.FC<{ label: string; value: string; tone?: 'default' | 'pos' | 'neg' | 'brand' | 'warn' }> = ({
    label,
    value,
    tone = 'default',
}) => {
    const ink =
        tone === 'pos'
            ? colors.pos
            : tone === 'neg'
            ? colors.neg
            : tone === 'warn'
            ? colors.warn
            : tone === 'brand'
            ? colors.brand
            : colors.ink00;
    const bg =
        tone === 'pos'
            ? colors.posSoft
            : tone === 'neg'
            ? colors.negSoft
            : tone === 'warn'
            ? colors.warnSoft
            : tone === 'brand'
            ? colors.brandSoft
            : colors.surface;
    return (
        <div
            style={{
                flex: '1 1 110px',
                padding: '10px 14px',
                borderRadius: 8,
                background: bg,
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    color: colors.ink40,
                    letterSpacing: 0.7,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                }}
            >
                {label}
            </div>
            <div style={{ fontSize: 22, color: ink, fontWeight: 700, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
        </div>
    );
};

const ScreenScoreGauge: React.FC<{ score?: number }> = ({ score }) => {
    const band = scoreBand(score);
    const color = bandColor(band);
    const r = 22;
    const c = 2 * Math.PI * r;
    const frac = scoreFraction(score);
    const dash = c * frac;
    return (
        <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
            <svg width={56} height={56} viewBox="0 0 56 56">
                <circle cx={28} cy={28} r={r} stroke={colors.hairline} strokeWidth={5} fill="none" />
                <circle
                    cx={28}
                    cy={28}
                    r={r}
                    stroke={color}
                    strokeWidth={5}
                    fill="none"
                    strokeDasharray={`${dash} ${c - dash}`}
                    strokeLinecap="round"
                    transform="rotate(-90 28 28)"
                />
            </svg>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    color,
                }}
            >
                {score ?? '—'}
            </div>
        </div>
    );
};

const ScreenDirectorCard: React.FC<{ p: DirectorDeepProfile }> = ({ p }) => {
    const band = scoreBand(p.creditScore);
    const tone = avatarTone(p.name);
    const hasCases = (p.legalCasesCount ?? 0) > 0;
    const otherBoards = p.currentEntities?.slice(0, 3) ?? [];
    const formerCount = p.formerEntities?.length ?? 0;
    return (
        <div
            style={{
                flex: '1 1 calc(50% - 6px)',
                minWidth: 280,
                padding: 14,
                border: `1px solid ${hasCases ? colors.neg : colors.rule}`,
                borderLeft: `3px solid ${hasCases ? colors.neg : bandColor(band)}`,
                borderRadius: 8,
                background: colors.paper,
                boxSizing: 'border-box',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        background: tone.bg,
                        color: tone.ink,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 14,
                        flexShrink: 0,
                    }}
                >
                    {initials(p.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: colors.ink00,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: colors.ink40, marginTop: 2 }}>{p.designation}</div>
                </div>
                <ScreenScoreGauge score={p.creditScore} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                <ScreenChip tone={bandTone(band)}>{scoreLabel(p.creditScore)}</ScreenChip>
                {p.creditBureau ? <ScreenChip tone="muted">{p.creditBureau}</ScreenChip> : null}
                {p.totalAccounts != null ? <ScreenChip tone="muted">{p.totalAccounts} accounts</ScreenChip> : null}
                {p.dpdAccounts != null && p.dpdAccounts > 0 ? <ScreenChip tone="HIGH">{p.dpdAccounts} DPD acc.</ScreenChip> : null}
                {p.maxDpds != null && p.maxDpds > 0 ? (
                    <ScreenChip tone={p.maxDpds > 30 ? 'HIGH' : 'MEDIUM'}>{p.maxDpds}d max DPD</ScreenChip>
                ) : null}
                {p.currentOverdue != null && p.currentOverdue > 0 ? (
                    <ScreenChip tone="HIGH">₹{Math.round(p.currentOverdue).toLocaleString('en-IN')} overdue</ScreenChip>
                ) : null}
                {p.enquiriesInPast90Days != null && p.enquiriesInPast90Days > 0 ? (
                    <ScreenChip tone="muted">{p.enquiriesInPast90Days} enq · 90d</ScreenChip>
                ) : null}
                {hasCases ? (
                    <ScreenChip tone="HIGH">
                        {p.legalCasesCount} case{(p.legalCasesCount ?? 0) > 1 ? 's' : ''}
                    </ScreenChip>
                ) : null}
                {p.currentEntities?.length ? (
                    <ScreenChip tone="muted">
                        {p.currentEntities.length} board{p.currentEntities.length > 1 ? 's' : ''}
                    </ScreenChip>
                ) : null}
                {formerCount ? <ScreenChip tone="muted">{formerCount} former</ScreenChip> : null}
                {p.phone ? <ScreenChip tone="muted">{p.phone}</ScreenChip> : null}
            </div>
            {otherBoards.length ? (
                <div style={{ borderTop: `1px dashed ${colors.hairlineSoft}`, paddingTop: 8 }}>
                    <div
                        style={{
                            fontSize: 9.5,
                            color: colors.ink40,
                            letterSpacing: 0.7,
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 4,
                        }}
                    >
                        Concurrent boards
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {otherBoards.map((e, i) => (
                            <div key={i} style={{ fontSize: 11.5, color: colors.ink10, lineHeight: 1.5 }}>
                                <span style={{ color: colors.muted }}>•</span> <span style={{ fontWeight: 600 }}>{e.name}</span>
                                {e.role ? <span style={{ color: colors.muted }}> — {e.role}</span> : null}
                            </div>
                        ))}
                    </div>
                    {(p.currentEntities?.length ?? 0) > otherBoards.length ? (
                        <div style={{ fontSize: 10.5, color: colors.muted, marginTop: 4 }}>
                            +{(p.currentEntities?.length ?? 0) - otherBoards.length} more
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

const ScreenDirectorsDeep: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const dd = data.directorsDeep;
    if (!dd) return null;
    const summary = buildSummary(dd.profiles);
    return (
        <ScreenSection
            kicker="Section · Directors"
            title="Director bureau & regulatory deep-dive"
            subtitle="Per-director CIBIL / Equifax score bands, legal exposure and concurrent directorships."
            index={index}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                <ScreenStatBox label="Profiled" value={String(summary.total)} tone="brand" />
                {summary.avgScore != null ? (
                    <ScreenStatBox
                        label="Avg score"
                        value={String(summary.avgScore)}
                        tone={summary.avgScore >= 750 ? 'pos' : summary.avgScore >= 650 ? 'warn' : 'neg'}
                    />
                ) : null}
                <ScreenStatBox
                    label="Legal exposure"
                    value={String(summary.totalCases)}
                    tone={summary.totalCases > 0 ? 'neg' : 'default'}
                />
                {summary.directorsWithCases > 0 ? (
                    <ScreenStatBox label="Directors w/ cases" value={String(summary.directorsWithCases)} tone="neg" />
                ) : null}
                {summary.maxBoards > 0 ? <ScreenStatBox label="Max boards" value={String(summary.maxBoards)} /> : null}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
                {dd.profiles.map((p, i) => (
                    <ScreenDirectorCard key={i} p={p} />
                ))}
            </div>
        </ScreenSection>
    );
};

export const directorsDeepModule: ModuleDefinition = {
    meta: {
        id: 'directorsDeep' as any,
        title: 'Director bureau & regulatory deep-dive',
        description: 'Bureau score bands, legal exposure, concurrent boards per director.',
        dataSource: 'polus/unifier/getCompanyOverviewDirectorDetails',
    },
    hasData: (d) => !!d.directorsDeep && d.directorsDeep.profiles.length > 0,
    Pdf: PdfDirectorsDeep,
    Screen: ScreenDirectorsDeep,
};
